import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { User, AuthChangeEvent } from '@supabase/supabase-js';

// Helper functions to access user metadata
export const getUserFirstName = (user: User | null): string => {
  return user?.user_metadata?.first_name || '';
};

export const getUserLastName = (user: User | null): string => {
  return user?.user_metadata?.last_name || '';
};

export const getUserFullName = (user: User | null): string => {
  const firstName = getUserFirstName(user);
  const lastName = getUserLastName(user);

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName) {
    return firstName;
  } else if (lastName) {
    return lastName;
  } else {
    return user?.email?.split('@')[0] || 'User';
  }
};

export const useUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const userRef = useRef<User | null>(null);

  // Function to check and set impersonation
  const checkImpersonation = (currentUser: User | null) => {
    if (!currentUser) {
      setImpersonatedUser(null);
      return;
    }

    const impersonatedUserId = localStorage.getItem("impersonatedUserId");
    const originalUserId = localStorage.getItem("originalUserId");

    if (impersonatedUserId && originalUserId) {
      const impersonated = {
        ...currentUser,
        id: impersonatedUserId,
        user_metadata: {
          ...currentUser.user_metadata,
          isImpersonated: true,
          originalUserId: currentUser.id
        }
      };
      setImpersonatedUser(impersonated);
    } else {
      setImpersonatedUser(null);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Get initial user
    const getInitialUser = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('useUser: Session error:', sessionError);
          if (mounted) {
            setError(sessionError.message);
            setLoading(false);
          }
          return;
        }

        if (!session) {
          if (mounted) {
            setUser(null);
            setError(null);
            setLoading(false);
          }
          return;
        }

        const { data: { user: initialUser }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          console.error('useUser: Error fetching user:', userError);
          if (mounted) {
            setError(userError.message);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setUser(initialUser);
          userRef.current = initialUser;
          checkImpersonation(initialUser);
          setLoading(false);
        }
      } catch (error) {
        console.error('useUser: Unexpected error in getInitialUser:', error);
        if (mounted) {
          setError(error instanceof Error ? error.message : 'An unexpected error occurred');
          setLoading(false);
        }
      }
    };

    getInitialUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      const currentUser = session?.user ?? null;

      // Handle different auth events
      switch (event) {
        case 'SIGNED_OUT':
          setUser(null);
          setError(null);
          setLoading(false);
          break;
        case 'SIGNED_IN':
          setUser(currentUser);
          setError(null);
          setLoading(false);
          break;
        case 'PASSWORD_RECOVERY':
          setUser(currentUser);
          setError(null);
          setLoading(false);
          break;
        case 'USER_UPDATED':
          setUser(currentUser);
          setError(null);
          break;
        case 'INITIAL_SESSION':
          setUser(currentUser);
          setLoading(false);
          break;
        default:
          setUser(currentUser);
      }

      userRef.current = currentUser;
      checkImpersonation(currentUser);
    });

    // Also listen for storage changes (in case impersonation is toggled in another tab)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "impersonatedUserId" || event.key === "originalUserId") {
        checkImpersonation(userRef.current);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Return the impersonated user if available, otherwise the actual user
  const currentUser = impersonatedUser || user;

  return {
    user: currentUser,
    actualUser: user,
    isImpersonating: !!impersonatedUser,
    loading,
    error,
    firstName: getUserFirstName(currentUser),
    lastName: getUserLastName(currentUser),
    fullName: getUserFullName(currentUser)
  };
};
